import { useTranslation } from '@tmsw/powerups.intl.hooks.use-translation';
import { useStore, useStoreApi } from '@tmsw/powerups.store';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { AudioDescriptions } from '@tmsw/powerups.services.audio-descriptions';
import { Hex, HexUtils, Vec2 } from '../HexGrid';
import { useSpriteSheet } from '../../../../../hooks/useSpriteSheet';
// import { usePitchTraversal } from '../../../../../hooks/usePitchTraversal';
import findFocusedHex from '../../services/findFocusedHex';
import GameCarousel from '../GameCarousel';
import GamePiecePreview from '../GamePiece/GamePiecePreview';
import HtmlGameBoard from '../HtmlGameBoard';
import SkipToCarousel from '../SkipToCarousel';
import { SvgGameFilters } from './img/filters';
import { useLevelContext } from '../../hooks/useLevel/provider';
import { getKeyboardEventMapItem } from '../../services/keyboardEventMap';
import './GameGrid.css';

export type GameGridProps = {
  [key: string]: any;
};

export function GameGrid({ config }: GameGridProps) {
  const { __t } = useTranslation('GAME_PLAY');

  // props
  const { gameLevel, theme, playSfx } = config;

  // local state
  const { state, actions } = useLevelContext();
  const { pieces, board, metadata } = state;
  // console.log(pieces, board, metadata, rest);

  // global state
  const { setState } = useStoreApi();
  const viewport = useStore((state) => state.viewport);
  const showGameBoard = useStore((state) => state.showGameBoard);
  const incrementMoves = useStore((state) => state.incrementMoves);
  const resetStreak = useStore((state) => state.resetStreak);
  const setActiveMiss = useStore((state) => state.setActiveMiss);
  const activeMiss = useStore((state) => state.activeMiss);
  const descriptionsEnabled = useStore((state) => state.descriptionsEnabled);
  const currentCueMetadata = useStore((state) => state.currentCueMetadata);

  // VIE .video-container: used to calculate the hexgame container size
  const videoContainerRef = useRef<HTMLDivElement>(
    document.querySelector('.video-container')
  );

  // VIE .UI-Overlay: used to calculate the hexgame container size
  // const UIOverlayRef = useRef(document.getElementById('UI-Overlay'));

  // parent layout element: background, fades in and out during progress interludes
  const containerRef = useRef<HTMLDivElement | null>(null);

  // bounding layout element: letterboxed to VIE video (within reason)
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // gameboard (the playable area)
  const gameboardRef = useRef<HTMLDivElement | null>(null);

  // carousel bounding element: used for keyboard traversal
  const carouselRef = useRef<HTMLDivElement | null>(null);

  const videoContainerBCR = videoContainerRef.current?.getBoundingClientRect();
  const containerWidth = videoContainerBCR?.width ?? viewport.width;
  const containerHeight =
    videoContainerBCR && videoContainerBCR?.height <= viewport.height
      ? videoContainerBCR?.height
      : viewport.height;
  const wrapperSize = {
    width: containerWidth * 0.9,
    height: containerHeight,
  };

  // initialize grid item props
  const initalGridItemProps = useMemo(() => {
    return {
      ready: false,
      layout: {
        size: {
          x: 10,
          y: 10,
        },
        orientation: {
          f0: 1.5,
          f1: 0,
          f2: 0.8660254037844386,
          f3: 1.7320508075688772,
          b0: 0.6666666666666666,
          b1: 0,
          b2: -0.3333333333333333,
          b3: 0.5773502691896257,
          startAngle: 0,
        },
        origin: {
          x: 0,
          y: 0,
        },
        spacing: 1,
      },
    };
  }, []);

  const refs = {
    // UIOverlayRef,
    containerRef,
    wrapperRef,
    gameboardRef,
    carouselRef,
  };

  const [gridItemProps, setGridItemProps] = useState(initalGridItemProps);
  const [progressText, setProgressText] = useState<string | null>(null);

  // const pitchTraversal = usePitchTraversal({
  //   calculateDistance: (focusedHex, selectedHex) => {
  //     const dist = HexUtils.distance(focusedHex, selectedHex);
  //     return dist * 2;
  //   },
  //   enabled: descriptionsEnabled,
  // });

  const gridVfxSprites = {
    placementSuccess: useSpriteSheet({
      name: 'placementSuccess',
      ref: useRef(null),
    }),
    placementError: useSpriteSheet({
      name: 'placementError',
      ref: useRef(null),
      // @ts-ignore
      onPause: (spritesheet) => {
        if (activeMiss) setActiveMiss(null);
      },
    }),
  };

  // @ts-ignore
  const triggerGridVfxSprite = ({ sprite, target }) => {
    const vfxSprite = document.getElementById(`gridVfx-${sprite.name}`);
    // @ts-ignore
    const vfxSpriteBox = vfxSprite.querySelector('.spriteBox');
    const bcr = target.getBoundingClientRect();
    // console.log('vfxSprite: target: bcr: ', bcr);
    // console.log('target.style.transform: ', target.style.transform);
    // @ts-ignore
    vfxSprite.style.transform = target.style.transform;
    // @ts-ignore
    vfxSprite.style.width = bcr.width + 'px';
    // @ts-ignore
    vfxSprite.style.height = bcr.height + 'px';
    // @ts-ignore
    vfxSpriteBox.style.transform = `scale(${
      bcr.width / 180
    }) translateX(-113%) translateY(-140%) `;
    sprite.ref.current.goToAndPlay(1);
  };

  const readAD = useCallback(
    (text: string) => {
      if (descriptionsEnabled) {
        AudioDescriptions.readDescription(text);
      }
    },
    [descriptionsEnabled]
  );

  /**
   * Focus the game board
   * @see focusGameboard
   */
  const focusGameboard = () => {
    if (!gameboardRef.current) return;
    // TODO: check if focus should be on board or first focusable item
    gameboardRef.current.querySelector('button')?.focus();
  };

  // @ts-ignore
  const updateSelectedHex = ({ id }) => {
    // @ts-ignore
    const sHex = pieces.find((h) => HexUtils.getID(h) === id);
    if (metadata.selected && HexUtils.equals(metadata.selected, sHex)) {
      readAD(__t(`unselected`));
      actions.clearSelectedItem();
      // TODO: check on reset focus here
    } else {
      readAD(__t('selected') + ' ' + sHex.description);
      actions.setSelectedItem(sHex);
      focusGameboard();
      playSfx('selectPiece');
    }
  };

  // @ts-ignore
  const handleOnDropSuccess = ({ sourceHex, isBonusPoint }) => {
    console.log('handleOnDropSuccess', { sourceHex, isBonusPoint });
    actions.updateItem({
      piece: { ...sourceHex, solved: true },
    });
    playSfx('tilePlacementSuccess');
    triggerGridVfxSprite({
      sprite: gridVfxSprites.placementSuccess,
      target: document.getElementById(sourceHex.id + '-dropzone'),
    });
    actions.clearSelectedItem();

    if (descriptionsEnabled) {
      const msg = __t('success') + sourceHex.description;
      AudioDescriptions.readDescription(msg);
    }

    // @ts-ignore
    setState((state) => {
      state.hitsCount += 1;
      state.movesCount += 1;
      state.streakCount += 1;
      state.hitPercentage = (state.hitsCount / state.movesCount) * 100;
      state.progressPercentage = (state.hitsCount / pieces.length) * 100;
    });
  };

  // @ts-ignore
  const handleOnDropFailure = ({ sourceHex, destHex }) => {
    console.log('handleOnDropFailure', { sourceHex });
    actions.clearSelectedItem();

    if (descriptionsEnabled) {
      AudioDescriptions.readDescription(__t('fail'));
    }

    playSfx('tilePlacementError');

    setActiveMiss({
      sourceId: sourceHex.id,
      destId: destHex.id,
    });

    triggerGridVfxSprite({
      sprite: gridVfxSprites.placementError,
      target: document.getElementById(destHex.id + '-dropzone'),
    });

    resetStreak();
    incrementMoves();
  };

  // @ts-ignore
  const handleOnDrop = ({ sourceHex, destHex, dropSuccess, isBonusPoint }) => {
    if (dropSuccess) {
      handleOnDropSuccess({ sourceHex, isBonusPoint });
    } else {
      handleOnDropFailure({ sourceHex, destHex });
    }
  };

  // @ts-ignore
  const calculateDirectionalCoords = ({ coords, direction }) => {
    let newCoords = coords.clone();
    switch (direction) {
      case 'UP':
        return newCoords.add(new Vec2(0, -1));
      case 'RIGHT':
        return newCoords.add(new Vec2(1, 0));
      case 'DOWN':
        return newCoords.add(new Vec2(0, 1));
      case 'LEFT':
        return newCoords.add(new Vec2(-1, 0));
      default: {
        throw new Error(`Invalid direction: ${direction}`);
      }
    }
  };

  // @ts-ignore
  const getElementAtCoords = (point) =>
    document.querySelector(
      '[data-x="' + point.x + '"][data-y="' + point.y + '"]'
    );

  // @ts-ignore
  const getGridItemElement = ({ target, direction }) => {
    const coords = new Vec2(
      Number(target.getAttribute('data-x')),
      Number(target.getAttribute('data-y'))
    );

    const newCoords = calculateDirectionalCoords({ coords, direction });
    // console.log('dir=', direction, 'coords=', coords, 'newCoords=', newCoords);

    let el = getElementAtCoords(newCoords);
    console.log('Board traversal:: new el', el);

    if (!el) {
      if (
        (direction === 'LEFT' || direction === 'RIGHT') &&
        newCoords.x <= board.size.x
      ) {
        el = getElementAtCoords(
          newCoords.clone().add(new Vec2(direction === 'LEFT' ? -1 : 1, 0))
        );
      }
    }
    return el;
  };

  // @ts-ignore
  const handleClick = (event) => {
    if (
      event.type === 'dblclick' ||
      event.type === 'click' ||
      event.type === 'mousedown'
      // event.type === 'touchstart' ||
      // event.code === 'Enter'
      // event.code === 'Space'
    ) {
      console.log('handleClick', {
        type: event.type,
        target: event.target,
        activeElement: document.activeElement,
        event,
      });

      if (
        document.activeElement &&
        document.activeElement === document.getElementById('Gameboard')
      ) {
        document.activeElement.querySelector('button')?.focus();
      }

      const targetHex = new Hex(
        // @ts-ignore
        ...event.target.value.split(',').map((v) => Number(v))
      );
      // @ts-ignore
      if (!targetHex || targetHex.solved) return;

      const focusedHex = findFocusedHex(pieces);

      // @ts-ignore
      if (!HexUtils.equals(targetHex, focusedHex)) {
        // console.log('handleClick :: Focused hex is not equal to current target. Setting focus!');
        event.target.focus();
      }

      if (!metadata.selected || typeof metadata.selected !== 'object') return;

      // @ts-ignore
      handleOnDrop({
        sourceHex: metadata.selected,
        destHex: targetHex,
        // @ts-ignore
        dropSuccess: HexUtils.equals(metadata.selected, targetHex),
      });
    }
  };

  /**
   * @name handleKeyDown
   * @param Event event
   * @returns
   */
  // @ts-ignore
  const handleKeyDown = (event: any) => {
    const keyConfig = getKeyboardEventMapItem({ event, mapPath: 'gameboard' });

    console.log('handleKeyDown', event.type, event.code, {
      type: event.type,
      code: event.code,
      target: event.target,
      keyConfig,
      activeElement: document.activeElement,
      event,
      metadata,
    });

    if (!document.activeElement) return;
    let target = document.activeElement;

    // TODO: check if needed. Board should not be directly focusable, but have focusin bubble up
    if (document.activeElement === document.getElementById('Gameboard')) {
      // @ts-ignore
      target = document.activeElement.querySelector('button');
      // li...?
    } else if (document.activeElement.tagName !== 'BUTTON') {
      // will refocus target later if this is a selection event
      // the focus needs to be on the div[role="gridcell"] for board traversal first
      // @ts-ignore
      target = document.activeElement.querySelector('button');
    }

    let el = null;

    if (typeof keyConfig === 'object') {
      event.preventDefault();
      el = getGridItemElement({ target, direction: keyConfig.direction });
      // jump to carousel when at the bottom
      if (!el && keyConfig.direction === 'DOWN' && carouselRef.current) {
        el = carouselRef.current?.querySelector('li');
      }
    }

    if (el) {
      // @ts-ignore
      el.focus();
    }

    //
    // THESE COMMANDS REQUIRE A GAME PIECE TO BE SELECTED...
    //
    if (!metadata.selected || typeof metadata.selected !== 'object') return;

    if (event.code === 'Enter' || event.code === 'Space') {
      if (target !== document.activeElement) {
        // @ts-ignore
        target.focus();
      }
      return void handleClick(event.nativeEvent);
    }

    // const focusedHex = findFocusedHex(pieces);
    // if (!focusedHex) return;
    // pitchTraversal(focusedHex, metadata.selected);
  };

  /**
   * onComplete Side Effect
   * @todo Need to override the abrupt VIE exit, allow full board reveal.
   *  a) hoist this code to GamePlay [OR]
   *  b) emit an event here for GamePlay to hook into
   *  c) add a chapter for VIE to handle and remove this code completely
   */
  useEffect(() => {
    const unsolved = pieces.filter((hex: any) => !hex.solved);
    console.log('Pieces remaining: ', unsolved.length);
    if (unsolved.length === 0) {
      // @ts-ignore
      containerRef.current.classList.add('game-outro');
    }
    const containerEl = containerRef.current;
    return () => {
      containerEl?.classList.remove('game-outro');
    };
  }, [pieces]);

  useEffect(() => {
    if (
      currentCueMetadata &&
      currentCueMetadata != null &&
      currentCueMetadata?.id?.length
    ) {
      if (currentCueMetadata?.id === 'TRIGGER_PROGRESS25-TEXT_IN') {
        setProgressText(config.progressLevel[0].label.toLocaleUpperCase());
      } else if (currentCueMetadata?.id === 'TRIGGER_PROGRESS50-TEXT_IN') {
        setProgressText(config.progressLevel[1].label.toLocaleUpperCase());
      } else if (currentCueMetadata?.id === 'TRIGGER_PROGRESS75-TEXT_IN') {
        setProgressText(config.progressLevel[2].label.toLocaleUpperCase());
      } else if (
        currentCueMetadata?.id === 'TRIGGER_PROGRESS25_OUT' ||
        currentCueMetadata?.id === 'TRIGGER_PROGRESS50_OUT' ||
        currentCueMetadata?.id === 'TRIGGER_PROGRESS75_OUT'
      ) {
        // @todo Creative asked this animate out (non-critical)
        setProgressText(null);
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [currentCueMetadata]);

  // TODO: double check these classes
  // function progressTextClassName() {
  //   if (progressText !== null) {
  //     if (
  //       (progressText as any).length > 6 &&
  //       (progressText as any).length <= 8
  //     ) {
  //       return 'progressText-wrapper long';
  //     } else if ((progressText as any).length > 8) {
  //       return 'progressText-wrapper super-long';
  //     } else {
  //       return 'progressText-wrapper';
  //     }
  //   }
  // }

  return (
    <>
      {typeof progressText === 'string' && (
        <div
          className={`progressText-wrapper ${
            progressText.length > 8
              ? 'super-long'
              : progressText.length > 6
              ? 'long'
              : ''
          }`}
          // TODO: should not need to depend on showGameboard. If it does, something is wrong.
          // hidden={!showGameBoard}
          // aria-hidden={!showGameBoard}
        >
          <h2 tabIndex={0}>{progressText}</h2>
        </div>
      )}
      <GamePiecePreview
        gridItemProps={gridItemProps}
        selectedPiece={metadata.selected}
        board={board}
        gameboardRef={refs.gameboardRef}
      />
      <div
        id="HexGame-Container"
        className={`hexgame-container theme-${theme.slug} ${
          showGameBoard ? '' : 'fade-out'
        }`}
        style={{
          backgroundImage: `url(${theme.bgImg})`,
        }}
        ref={containerRef}
        data-theme={theme.slug}
      >
        {/* TODO: move fade here and make .video-container transparent */}
        <div
          className="hexgame-wrapper "
          ref={wrapperRef}
          style={{
            width: wrapperSize.width,
            height: wrapperSize.height,
          }}
        >
          <SkipToCarousel />
          <div className="hexgame-content">
            <div className="hexgame-header">
              <img
                src={__t('logos.primary', 'GENERAL')}
                alt={__t('logoImageAlt', 'GENERAL')}
              />
            </div>
            <div className="hexgame-board hide">
              <div
                className="hexgame-board-frame"
                role="presentation"
                style={{
                  backgroundImage: `url(${theme.puzzleFrame})`,
                }}
              />
              <div
                className="hexgame-board-mask-outer"
                role="presentation"
                style={{
                  backgroundImage: `url(${theme.puzzleMaskOuter})`,
                }}
              />
              <div
                id="BoardBackground"
                className="hexgame-board-background"
                role="presentation"
                style={{
                  backgroundImage: `url(${board.boardImg})`,
                }}
              ></div>
              <HtmlGameBoard
                pieces={pieces}
                board={board}
                actions={actions}
                gameLevel={gameLevel}
                selectedPiece={metadata.selected}
                handleKeyDown={handleKeyDown}
                handleClick={handleClick}
                theme={theme}
                setGridItemProps={setGridItemProps}
                gridItemProps={gridItemProps}
                refs={refs}
                viewport={viewport}
              />
              <div id="gridVfx">
                {Object.values(gridVfxSprites).map((sprite) => (
                  <div
                    key={sprite.name}
                    className="gridVfx-Sprite"
                    id={`gridVfx-${sprite.name}`}
                  >
                    <div
                      className="spriteBox nonInteract"
                      id={`gridVfx-spriteBox-${sprite.name}`}
                    >
                      {sprite.spritesheet}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <GameCarousel
              carouselRef={carouselRef}
              theme={theme}
              itemsToShow={3}
              board={board}
              carouselItems={pieces
                .filter((hex: any) => !hex.solved)
                // @ts-ignore
                .sort((h1, h2) => h1.shuffleIndex - h2.shuffleIndex)}
              updateSelected={updateSelectedHex}
              handleOnDrop={handleOnDrop}
              gridItemProps={gridItemProps}
              playSfx={playSfx}
            />
            <SvgGameFilters />
          </div>
        </div>
      </div>
    </>
  );
}
